import React, { useState } from 'react';
import CodeBlock from '../codeBlock';
import TerminalOutput from '../terminalOutput';
import { SmartTextRenderer, EnhancedCodeBlock } from '../ui/MathRenderer';
import { QuestionMedia } from '../ui/QuestionMedia';

/**
 * QuestionStatsTab Component
 * 
 * Per-question analysis with difficulty assessment
 * Features:
 * - Correct/incorrect breakdown
 * - Option distribution visualization
 * - Difficulty rating (Easy/Medium/Hard)
 * - Common mistakes identification
 * - Expandable question details
 */
function QuestionStatsTab({ stats, instance, participantCount }) {
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Leicht':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50';
      case 'Mittel':
        return 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/50';
      case 'Schwer':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/50';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  // Get correct rate color
  const getCorrectRateColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate >= 80) return 'text-green-600 dark:text-green-400';
    if (numRate >= 50) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (stats.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Keine Fragen</h3>
        <p className="text-gray-500 dark:text-gray-400">Dieses Quiz hat noch keine Fragen.</p>
      </div>
    );
  }

  // Calculate overall stats
  const totalQuestions = stats.length;
  const averageCorrectRate = (stats.reduce((sum, q) => sum + parseFloat(q.correctRate), 0) / totalQuestions).toFixed(1);
  const easyCount = stats.filter(q => q.difficulty === 'Leicht').length;
  const mediumCount = stats.filter(q => q.difficulty === 'Mittel').length;
  const hardCount = stats.filter(q => q.difficulty === 'Schwer').length;

  return (
    <div>
      {/* Overall Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-primary-50 dark:bg-primary-900/30 rounded-lg p-4 border border-primary-200 dark:border-primary-700">
          <div className="text-sm text-primary-600 dark:text-primary-400 font-medium">Fragen gesamt</div>
          <div className="text-2xl font-bold text-primary-900 dark:text-primary-300">{totalQuestions}</div>
        </div>
        <div className="bg-secondary-50 dark:bg-secondary-900/30 rounded-lg p-4 border border-secondary-200 dark:border-secondary-700">
          <div className="text-sm text-secondary-600 dark:text-secondary-400 font-medium">Ø Richtig</div>
          <div className="text-2xl font-bold text-secondary-900 dark:text-secondary-300">{averageCorrectRate}%</div>
        </div>
        <div className="bg-success-50 dark:bg-success-900/30 rounded-lg p-4 border border-success-200 dark:border-success-700">
          <div className="text-sm text-success-600 dark:text-success-400 font-medium">Leichte Fragen</div>
          <div className="text-2xl font-bold text-success-900 dark:text-success-300">{easyCount}</div>
        </div>
        <div className="bg-warning-50 dark:bg-warning-900/30 rounded-lg p-4 border border-warning-200 dark:border-warning-700">
          <div className="text-sm text-warning-600 dark:text-warning-400 font-medium">Mittel</div>
          <div className="text-2xl font-bold text-warning-900 dark:text-warning-300">{mediumCount}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">+ {hardCount} Schwer</div>
        </div>
      </div>

      {/* Question List */}
      <div className="space-y-4">
        {stats.map((stat) => {
          const isExpanded = expandedQuestion === stat.questionIndex;
          const question = instance.questions[stat.questionIndex];
          const mostChosen = Object.entries(stat.optionCounts).sort((a, b) => b[1] - a[1])[0];
          const mostChosenIndex = parseInt(mostChosen[0]);
          // correctAnswer is an array, check if most chosen is NOT in the correct answers
          const correctAnswerIndices = Array.isArray(stat.correctAnswer) ? stat.correctAnswer : [stat.correctAnswer];
          const isCommonMistake = !correctAnswerIndices.includes(mostChosenIndex) && mostChosen[1] > 0;

          return (
            <div
              key={stat.questionIndex}
              className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Question Header */}
              <button
                onClick={() => setExpandedQuestion(isExpanded ? null : stat.questionIndex)}
                className="w-full px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                    Q{stat.questionIndex + 1}
                  </div>
                  <div className="flex-1">
                    <div className="text-base font-medium text-gray-900 dark:text-white line-clamp-2">
                      <SmartTextRenderer text={stat.question} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 ml-4">
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${getCorrectRateColor(stat.correctRate)}`}>
                      {stat.correctRate}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.correctAnswers}/{stat.totalAnswers} richtig
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(stat.difficulty)}`}>
                    {stat.difficulty}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 dark:text-gray-500 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700">
                  {/* Full Question */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Frage:</h4>
                    <div className="text-base text-gray-900 dark:text-white mb-3"><SmartTextRenderer text={stat.question} /></div>

                    {/* Code Block */}
                    {question.showCodeBlock && question.code && (
                      <div className="mb-3">
                        <CodeBlock code={question.code} />
                      </div>
                    )}

                    {/* New codeBlock format with LaTeX support */}
                    {question.codeBlock && question.codeBlock.code && (
                      <div className="mb-3">
                        <EnhancedCodeBlock 
                          code={question.codeBlock.code} 
                          language={question.codeBlock.language} 
                        />
                      </div>
                    )}

                    {/* Media/Image */}
                    {question.media && (
                      <div className="mb-3">
                        <QuestionMedia media={question.media} />
                      </div>
                    )}

                    {/* Terminal Output */}
                    {question.showTerminal && question.terminalOutput && (
                      <div className="mb-3">
                        <TerminalOutput output={question.terminalOutput} />
                      </div>
                    )}
                  </div>

                  {/* Response Distribution */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Antwortverteilung:</h4>
                    <div className="space-y-2">
                      {stat.options.map((option, index) => {
                        const count = stat.optionCounts[index] || 0;
                        const percentage = stat.totalAnswers > 0 ? (count / stat.totalAnswers) * 100 : 0;
                        // correctAnswer is an array, so check if index is in the array
                        const correctAnswerIndices = Array.isArray(stat.correctAnswer) ? stat.correctAnswer : [stat.correctAnswer];
                        const isCorrect = correctAnswerIndices.includes(index);
                        const isPopularWrong = index === mostChosenIndex && isCommonMistake;

                        return (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border-2 ${
                              isCorrect
                                ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                                : isPopularWrong
                                ? 'border-red-300 bg-red-50 dark:bg-red-900/30'
                                : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className={`text-sm font-semibold ${isCorrect ? 'text-green-700 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                                  {String.fromCharCode(65 + index)}
                                </span>
                                <span className="text-sm text-gray-900 dark:text-white"><SmartTextRenderer text={option} /></span>
                                {isCorrect && (
                                  <span className="px-2 py-0.5 bg-green-600 text-white text-xs font-semibold rounded">
                                    RICHTIG
                                  </span>
                                )}
                                {isPopularWrong && (
                                  <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-semibold rounded">
                                    HÄUFIGER FEHLER
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 text-sm">
                                <span className="text-gray-600 dark:text-gray-400">{count} Antworten</span>
                                <span className={`font-semibold ${isCorrect ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            {/* Progress Bar */}
                            <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-500 ${
                                  isCorrect ? 'bg-green-500' : isPopularWrong ? 'bg-red-400' : 'bg-blue-400'
                                }`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stats Summary */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Antworten gesamt</div>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {stat.totalAnswers}/{participantCount}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {participantCount > 0 ? ((stat.totalAnswers / participantCount) * 100).toFixed(0) : 0}% Antwortrate
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Richtige Antworten</div>
                      <div className="text-lg font-semibold text-success-600 dark:text-success-400">{stat.correctAnswers}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">{stat.correctRate}% richtig beantwortet</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Falsche Antworten</div>
                      <div className="text-lg font-semibold text-danger-600 dark:text-danger-400">{stat.incorrectAnswers}</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {stat.totalAnswers > 0 ? ((stat.incorrectAnswers / stat.totalAnswers) * 100).toFixed(0) : 0}% falsch
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default QuestionStatsTab;
